import React, { useEffect, useMemo, useState } from 'react';
import {
  createProviderSearch,
  getProviderSearches,
  refreshProviderSearch,
  type GateDeliverable,
  type ProviderSearchCandidate,
  type ProviderSearchRecord,
  type RequirementSourceRef,
  type SelaProjectDetail,
} from '../../services/selaApi';
import { recordV2ErrorStage, recordV2FunnelStep } from '../../utils/v2Rum';

const ROLE_OPTIONS = [
  'Geotechnical engineer',
  'Surveyor / topographic surveyor',
  'Arborist',
  'Civil engineer',
  'Architect / designer',
  'Permit consultant / expediter',
  'Land-use attorney / consultant',
  'General contractor / builder',
  'Other',
];

const MATCH_LABEL: Record<string, string> = {
  strong_match: 'Strong match',
  possible_match: 'Possible match',
  weak_match: 'Weak match',
  needs_review: 'Needs review',
};

const MATCH_TONE: Record<string, string> = {
  strong_match: 'bg-emerald-50 text-emerald-700',
  possible_match: 'bg-blue-50 text-blue-700',
  weak_match: 'bg-amber-50 text-amber-700',
  needs_review: 'bg-slate-100 text-slate-700',
};

type Props = {
  project: SelaProjectDetail;
  deliverable: GateDeliverable;
  sourceRef: RequirementSourceRef;
  onClose: () => void;
};

function shortDate(value?: string): string {
  if (!value) return '';
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return value;
  return new Date(timestamp).toLocaleDateString();
}

function locationText(project: SelaProjectDetail): string {
  if (project.city && project.state) return `${project.city}, ${project.state}`;
  if (project.city) return project.city;
  return project.address || 'Project area';
}

function licensingNote(role: string): string | null {
  const lowered = role.toLowerCase();
  if (/(engineer|surveyor|architect|attorney|contractor|builder|arborist)/.test(lowered)) {
    return 'Verify credentials, license status where applicable, insurance, availability, scope, and fit before hiring.';
  }
  return null;
}

function matchLabel(match: string): string {
  return MATCH_LABEL[match] || match.replace(/_/g, ' ') || 'Needs review';
}

function sourceHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function ProviderCard({ provider }: { provider: ProviderSearchCandidate }) {
  const tone = MATCH_TONE[provider.matchQuality] || MATCH_TONE.needs_review;
  return (
    <article className="rounded-xl border border-[#dbe4f0] bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-[#0f172a]">{provider.name}</h3>
          <div className="mt-1 flex flex-wrap gap-2 text-xs text-[#647488]">
            <span>{provider.role}</span>
            {provider.locationOrServiceArea ? <span>{provider.locationOrServiceArea}</span> : null}
          </div>
        </div>
        <span className={['rounded-full px-2 py-0.5 text-[10px] font-semibold', tone].join(' ')}>
          {matchLabel(provider.matchQuality)}
        </span>
      </div>

      {provider.matchRationale ? (
        <div className="mt-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[#647488]">Why this may fit</div>
          <p className="mt-1 text-sm leading-relaxed text-[#334155]">{provider.matchRationale}</p>
        </div>
      ) : null}

      {provider.evidenceSnippets.length ? (
        <div className="mt-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[#647488]">Evidence</div>
          <ul className="mt-1 space-y-1 text-sm text-[#334155]">
            {provider.evidenceSnippets.map((snippet, index) => (
              <li key={`${provider.name}-evidence-${index}`}>“{snippet}”</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-3 grid gap-2 text-xs text-[#475669] md:grid-cols-2">
        <div>
          <span className="font-semibold text-[#0f172a]">Website: </span>
          {provider.websiteUrl ? (
            <a className="text-[#3251b7] hover:underline" href={provider.websiteUrl} target="_blank" rel="noreferrer">
              {sourceHost(provider.websiteUrl)}
            </a>
          ) : 'Not found'}
        </div>
        <div>
          <span className="font-semibold text-[#0f172a]">Contact: </span>
          {[provider.contactEmail, provider.phone].filter(Boolean).join(' · ') || 'Not found'}
        </div>
        {provider.contactPageUrl ? (
          <div>
            <span className="font-semibold text-[#0f172a]">Contact page: </span>
            <a className="text-[#3251b7] hover:underline" href={provider.contactPageUrl} target="_blank" rel="noreferrer">
              {sourceHost(provider.contactPageUrl)}
            </a>
          </div>
        ) : null}
        {provider.suggestedNextStep ? (
          <div>
            <span className="font-semibold text-[#0f172a]">Next step: </span>
            {provider.suggestedNextStep}
          </div>
        ) : null}
      </div>

      {provider.sourceUrls.length ? (
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          {provider.sourceUrls.map((url) => (
            <a key={url} className="rounded-full border border-[#cdd5e1] px-2 py-1 font-semibold text-[#1e4474] hover:border-[#3251b7]" href={url} target="_blank" rel="noreferrer">
              Source: {sourceHost(url)}
            </a>
          ))}
        </div>
      ) : null}

      {provider.warnings.length ? (
        <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {provider.warnings.join(' ')}
        </div>
      ) : null}
    </article>
  );
}

export function SelaProviderSearchModal({ project, deliverable, sourceRef, onClose }: Props) {
  const [searches, setSearches] = useState<ProviderSearchRecord[]>([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [location, setLocation] = useState(locationText(project));
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const latest = searches[0];
  const resolvedRole = selectedRole === 'Other' ? customRole.trim() : selectedRole.trim();
  const displayRole = resolvedRole || latest?.neededRole || deliverable.suggestedExpert || deliverable.ownerRole || 'Provider';
  const note = licensingNote(displayRole);

  const relevantRisks = useMemo(
    () => [deliverable.relatedRiskOrMissingFact, deliverable.whyItMatters].filter(Boolean) as string[],
    [deliverable.relatedRiskOrMissingFact, deliverable.whyItMatters],
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const rows = await getProviderSearches(project.id, { deliverableId: deliverable.deliverableId });
        if (!cancelled) {
          setSearches(rows);
          if (rows[0]?.neededRole) setSelectedRole(rows[0].neededRole);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load provider searches.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [deliverable.deliverableId, project.id]);

  const runSearch = async (refresh = false) => {
    setBusy(true);
    setError(null);
    recordV2FunnelStep('roadmap_provider_search', 'start', {
      deliverable_id: deliverable.deliverableId,
      refresh,
      source_page: 'roadmap',
    }, project.id);
    try {
      const payload = {
        deliverableId: deliverable.deliverableId,
        requirementRef: sourceRef,
        providerRole: resolvedRole || undefined,
        locationText: location,
        knownRisks: relevantRisks,
      };
      const next = refresh && latest
        ? await refreshProviderSearch(project.id, latest.searchId, payload)
        : await createProviderSearch(project.id, payload);
      setSearches((current) => [next, ...current.filter((item) => item.searchId !== next.searchId)]);
      if (next.neededRole) setSelectedRole(next.neededRole);
      recordV2FunnelStep('roadmap_provider_search', 'success', {
        deliverable_id: deliverable.deliverableId,
        refresh,
        provider_count: next.providers?.length || 0,
        source_page: 'roadmap',
      }, project.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search providers.');
      recordV2FunnelStep('roadmap_provider_search', 'failure', { source_page: 'roadmap' }, project.id);
      recordV2ErrorStage('roadmap_provider_search', err, {}, project.id);
    } finally {
      setBusy(false);
    }
  };

  const providers = latest?.providers || [];
  const emptyMessage = latest?.fallbackMessage || 'Sela could not find strong provider matches. Try broadening the location, changing the provider role, or using the shown search terms externally.';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6">
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-2xl border border-[#d8e0eb] bg-[#f8fafc] shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-[#d8e0eb] bg-white px-5 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[#647488]">Provider Finder</p>
            <h2 className="mt-1 text-lg font-bold text-[#0f172a]">Find Providers</h2>
            <p className="mt-1 text-sm text-[#647488]">{deliverable.label}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md px-2 py-1 text-sm font-semibold text-[#647488] hover:bg-slate-100" aria-label="Close">
            ×
          </button>
        </div>

        <div className="overflow-auto px-5 py-4">
          <div className="rounded-xl border border-[#dbe4f0] bg-white p-4">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-[#647488]">Action</div>
                <div className="mt-1 text-sm font-semibold text-[#0f172a]">{deliverable.label}</div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-[#647488]">Needed role</div>
                <div className="mt-1 text-sm font-semibold text-[#0f172a]">{displayRole}</div>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-[#647488]">Provider role override</label>
                <select value={selectedRole} onChange={(event) => setSelectedRole(event.target.value)} className="mt-1 w-full rounded-md border border-[#cdd5e1] bg-white px-3 py-2 text-sm">
                  <option value="">Infer from action</option>
                  {ROLE_OPTIONS.map((role) => <option key={role} value={role}>{role}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-[#647488]">Location</label>
                <input value={location} onChange={(event) => setLocation(event.target.value)} className="mt-1 w-full rounded-md border border-[#cdd5e1] px-3 py-2 text-sm" />
              </div>
              {selectedRole === 'Other' ? (
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wide text-[#647488]">Custom role</label>
                  <input value={customRole} onChange={(event) => setCustomRole(event.target.value)} placeholder="Provider role" className="mt-1 w-full rounded-md border border-[#cdd5e1] px-3 py-2 text-sm" />
                </div>
              ) : null}
            </div>

            <div className="mt-4 rounded-lg bg-[#f8fafc] px-3 py-2 text-xs leading-relaxed text-[#475669]">
              These providers were found through web search and have not been vetted by Sela. Verify credentials, availability, scope, insurance, and fit before hiring.
              {note ? <span> {note}</span> : null}
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs text-[#647488]">
                {latest?.searchedAt ? `Provider results last searched ${shortDate(latest.searchedAt)}.` : 'No provider search has been run for this action yet.'}
                {latest?.searchQuery ? <span> Query: {latest.searchQuery}</span> : null}
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" disabled={busy || loading} onClick={() => void runSearch(false)} className="rounded-md bg-[#3251b7] px-3 py-2 text-xs font-semibold text-white hover:bg-[#28449f] disabled:cursor-wait disabled:opacity-50">
                  {busy ? 'Searching...' : providers.length ? 'Search again' : 'Search providers'}
                </button>
                {latest ? (
                  <button type="button" disabled={busy || loading} onClick={() => void runSearch(true)} className="rounded-md border border-[#cdd5e1] bg-white px-3 py-2 text-xs font-semibold text-[#1e4474] hover:border-[#3251b7] disabled:cursor-wait disabled:opacity-50">
                    Refresh search
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {error ? <div className="mt-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}
          {latest?.status === 'failed' && latest.errorMessage ? <div className="mt-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{latest.errorMessage}</div> : null}

          <div className="mt-4">
            {loading ? (
              <div className="rounded-xl border border-dashed border-[#cdd5e1] bg-white p-5 text-sm text-[#647488]">Loading provider search history...</div>
            ) : providers.length ? (
              <div className="grid gap-3 xl:grid-cols-2">
                {providers.map((provider, index) => <ProviderCard key={`${provider.name}-${index}`} provider={provider} />)}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-[#cdd5e1] bg-white p-5 text-sm text-[#647488]">
                <div className="font-semibold text-[#0f172a]">{latest?.status === 'complete' ? 'No strong matches found.' : 'Ready to search.'}</div>
                <p className="mt-1">{emptyMessage}</p>
                {latest?.searchTerms?.length ? <p className="mt-2">Search terms: {latest.searchTerms.join(', ')}</p> : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

